import styles from './recetas-page.module.scss';
import React from "react";

import RecipeCard from "../../components/recipe-card/recipe-card";
import Hero from "../../components/hero/hero";
import { HERO_WAVE_PAGE } from "../../components/hero/waves";
import Paginator from "../../components/paginator/paginator";
import SearchFilter from "../../components/search-filter/search-filter";
import api, { Recipe } from "../../services/api";

export interface RecetasPageProps {
  name?: string;
  classes?: string;
}

/** Recetas por página (2 filas de 3 en desktop). */
const PER_PAGE = 6;

interface State {
  recipes: Recipe[] | null;
  loading: boolean;
  error?: string;
  /** Página actual (1-based). */
  page: number;
  /** Término de búsqueda actual (para el mensaje de "sin resultados"). */
  query: string;
}

/**
 * Recetario (Figma `110:529`): cabecera + "Nuestras recetas" + buscador +
 * listado paginado. Los datos vienen del API público (`/api/recipes`).
 */
export default class RecetasPage extends React.Component<RecetasPageProps, State> {
  static jsClass = "RecetasPage";
  static defaultProps: Partial<RecetasPageProps> = {
    name: "mbRecetas",
  };

  state: State = { recipes: null, loading: true, page: 1, query: "" };

  componentDidMount() {
    this.loadRecipes("");
  }

  /** Carga el recetario (con búsqueda `q` opcional) y vuelve a la página 1. */
  private loadRecipes = (q = "") => {
    this.setState({ loading: true, query: q, page: 1 });
    api
      .listRecipes(q)
      .then((recipes) => this.setState({ recipes, loading: false }))
      .catch((e) => this.setState({ error: String(e?.message ?? e), loading: false }));
  };

  render(): React.ReactNode {
    const { classes } = this.props;
    const { recipes, loading, error, page, query } = this.state;

    const totalPages = recipes ? Math.max(1, Math.ceil(recipes.length / PER_PAGE)) : 1;
    const current = Math.min(Math.max(page, 1), totalPages);
    const visible = recipes
      ? recipes.slice((current - 1) * PER_PAGE, current * PER_PAGE)
      : [];

    return (
      <div className={styles['mb-home'] + (classes ? ' ' + classes : '')}>
        {/* ── Cabecera (hero curvo, como las demás secciones) ────────────── */}
        <Hero
          src="/assets/hero/factory.jpg"
          title="Recetario Mangibone"
          tagline="Recetas que inspiran, sabores que se comparten."
          align="start"
          wave={HERO_WAVE_PAGE}
        />

        {/* ── Nuestras recetas ───────────────────────────────────────────── */}
        <section className={styles['mb-intro']}>
          <div className={styles['mb-wrap']}>
            <div className={styles['mb-intro__grid']}>
              <div className={styles['mb-intro__text']}>
                <h2 className={styles['mb-h2']}>Nuestras recetas</h2>
                <p>
                  Descubre cómo disfrutar nuestros productos Mangibone en recetas
                  fáciles, tradicionales y creativas, perfectas para cualquier
                  momento del día.
                </p>
                <p>
                  Desde desayunos hasta platillos especiales, aquí encontrarás
                  ideas para sacar el mejor sabor de cada queso.
                </p>
              </div>
              <img
                className={styles['mb-intro__img']}
                src="/assets/figma/receta-componente/receta-longaniza-portada.png"
                alt="Recetas Mangibone"
              />
            </div>
          </div>
        </section>

        <SearchFilter
          placeholder="Buscar en el recetario…"
          onSearch={this.loadRecipes}
        />

        {/* ── Listado ────────────────────────────────────────────────────── */}
        <section className={styles['mb-grid-section']}>
          <div className={styles['mb-wrap']}>
            {loading ? (
              <p className={styles['mb-state']}>Cargando recetario…</p>
            ) : error ? (
              <p className={styles['mb-state']}>No se pudo cargar el recetario ({error}).</p>
            ) : !recipes || recipes.length === 0 ? (
              <p className={styles['mb-state']}>
                {query
                  ? `No se encontraron recetas para «${query}».`
                  : "Todavía no hay recetas publicadas."}
              </p>
            ) : (
              <div className={styles['mb-recipes']}>
                {visible.map((recipe) => (
                  <RecipeCard key={recipe.slug} recipe={recipe} />
                ))}
              </div>
            )}
          </div>
        </section>

        {!loading && !error && totalPages > 1 && (
          <Paginator
            page={current}
            total={totalPages}
            onChange={(p) => this.setState({ page: p })}
          />
        )}
      </div>
    );
  }
}
